/**
 * Pose-based forehand/backhand classification (prototype).
 *
 * MoveNet returns 17 COCO keypoints. We use the shoulders to define the
 * player's own left↔right axis, then project the ball-contact point onto that
 * axis to decide which anatomical side the contact is on. Combined with the
 * player's known dominant hand, that gives a forehand/backhand guess that is
 * (unlike a raw image-x comparison) robust to which way the player faces.
 *
 * ⚠️ This is a heuristic guess: pose L/R labels can flip on blurry/occluded
 * frames, and two-handed backhands blur the signal. Always keep it human-verified.
 */

// MoveNet keypoint indices
export const KP = {
  nose: 0,
  leftShoulder: 5,
  rightShoulder: 6,
  leftWrist: 9,
  rightWrist: 10,
  leftHip: 11,
  rightHip: 12,
};

function centroid(keypoints, minScore) {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const k of keypoints) {
    if ((k.score ?? 0) >= minScore) {
      sx += k.x;
      sy += k.y;
      n++;
    }
  }
  return n ? { x: sx / n, y: sy / n, n } : null;
}

/** Choose the pose whose body is closest to a point (e.g. the ball at contact). */
export function nearestPose(poses, point, minScore = 0.2) {
  let best = null;
  let bestD = Infinity;
  for (const pose of poses) {
    const c = centroid(pose.keypoints, minScore);
    if (!c) continue;
    const d = Math.hypot(c.x - point.x, c.y - point.y);
    if (d < bestD) {
      bestD = d;
      best = pose;
    }
  }
  return best;
}

/**
 * Classify a shot as forehand/backhand for a given pose + ball-contact point.
 * Returns { type, side, confidence } or null when shoulders aren't reliable.
 */
export function classifyHandedness(pose, ball, dominantHand = "right", minScore = 0.3) {
  if (!pose?.keypoints) return null;
  const ls = pose.keypoints[KP.leftShoulder];
  const rs = pose.keypoints[KP.rightShoulder];
  if (!ls || !rs || (ls.score ?? 0) < minScore || (rs.score ?? 0) < minScore) {
    return null;
  }

  const cx = (ls.x + rs.x) / 2;
  const cy = (ls.y + rs.y) / 2;
  const svx = rs.x - ls.x; // shoulder axis: left → right (anatomical)
  const svy = rs.y - ls.y;
  const axisLen = Math.hypot(svx, svy) || 1;

  const relx = ball.x - cx;
  const rely = ball.y - cy;
  const proj = (relx * svx + rely * svy) / axisLen; // >0 toward the person's right

  const side = proj >= 0 ? "right" : "left";
  const type = side === dominantHand ? "forehand" : "backhand";

  // crude confidence: how far off the body midline the contact is, in shoulder-widths
  const confidence = Math.min(1, Math.abs(proj) / axisLen);
  return { type, side, confidence };
}
